import { z } from 'zod';

import type { RuntimeConfig } from './runtimeConfig.types';

const runtimeConfigSchema = z.object({
  apiBaseUrl: z.string().min(1, 'apiBaseUrl is required'),
  moarApiUrl: z.string().nullable(),
  sdkApiBaseUrl: z.string().nullable().default(null),
  tokensApiBaseUrl: z.string().nullable().default(null),
  signalsApiBaseUrl: z.string().nullable().default(null),
  servicesApiBaseUrl: z.string().nullable().default(null),
  missionsApiBaseUrl: z.string().nullable().default(null),
  datasetsApiBaseUrl: z.string().nullable().default(null),
  searchApiBaseUrl: z.string().nullable().default(null),
  entitiesApiBaseUrl: z.string().nullable().default(null),
  // Accepts any string so E2E/CI runners emitting "test", "ci", or "unknown"
  // do not trigger a validation crash. Known values: production,
  // development, local. Unknown values are preserved as-is.
  environment: z.string().default('local'),
  aws: z.object({
    region: z.string().min(1, 'aws.region is required'),
  }),
  cognito: z.object({
    userPoolId: z.string().min(1, 'cognito.userPoolId is required'),
    clientId: z.string().min(1, 'cognito.clientId is required'),
    endpoint: z.string().nullable(),
  }),
  stripe: z.object({
    publishableKey: z.string().nullable(),
  }),
  rum: z.object({
    enabled: z.boolean(),
    appId: z.string().nullable(),
    identityPoolId: z.string().nullable(),
    guestRoleArn: z.string().nullable().default(null),
    endpoint: z.string().nullable(),
  }),
  siteUrl: z.string().min(1, 'siteUrl is required'),
  jwtDemoUrl: z.string().min(1, 'jwtDemoUrl is required'),
  mcpBaseUrl: z.string().nullable(),
  mockoonRequiresAuth: z.boolean(),
  missionsFixtureMode: z.boolean().default(false),
  // GW-9653 (demo, epic GW-9650): Function URL for the phone-resolution demo
  // Lambda (gw-phone-demo-dev). Null in environments where the throwaway demo
  // Lambda is not deployed. Consumed only by src/api/phoneDemoApi.ts.
  phoneDemoFunctionUrl: z.string().nullable().default(null),
  // GW-10389 (demo, epic GW-9650): Function URL for the company-resolution demo
  // Lambda (gw-company-demo-dev). Null where the throwaway demo Lambda is not
  // deployed. Consumed only by src/api/companyDemoApi.ts.
  companyDemoFunctionUrl: z.string().nullable().default(null),
  // GW-10460 (demo, epic GW-10456): Function URL for the company-intel GraphIQ
  // demo Lambda. Null where the throwaway demo Lambda is not deployed. Consumed
  // only by the /company-intel demo page.
  companyIntelFunctionUrl: z.string().nullable().default(null),
  // GW-10389 (demo, epic GW-9650): comma-separated email allowlist gating the
  // demo search short-circuits. Null/empty disables the diverts for everyone.
  demoAllowlist: z.string().nullable().default(null),
  // GW-8883 (PRP-PLAT-026 Task 2): notifications AppSync Events transport
  // config. Null in environments with no AppSync Events API provisioned
  // (e.g. demo, or local dev with no equivalent mock). Consumed by the
  // connection manager landing in a later task — not wired to any UI yet.
  notificationsAppSync: z
    .object({
      endpoint: z.string().min(1, 'notificationsAppSync.endpoint is required'),
      region: z.string().min(1, 'notificationsAppSync.region is required'),
    })
    .nullable()
    .default(null),
});

let validated: RuntimeConfig | null = null;

export function getConfig(): RuntimeConfig {
  if (validated) return validated;

  const raw = (globalThis as unknown as Window).__RUNTIME_CONFIG__;
  if (!raw) {
    throw new Error(
      'Runtime config not available. Ensure env-config.js is loaded before app scripts.'
    );
  }

  const result = runtimeConfigSchema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(', ');
    throw new Error(`Runtime config validation failed: ${issues}`);
  }

  validated = result.data as RuntimeConfig;
  return validated;
}

/** Reset validated cache -- test use only. */
export function _resetConfigForTesting(): void {
  validated = null;
}
